import { Box, Center, Spinner, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { FeedPagesViewer } from "./FeedPagesViewer";
import { FeedComicInfoCard } from "./FeedComicInfoCard";
import { FeedFullscreenChrome } from "./FeedFullscreenChrome";
import { useFeedReleaseData } from "./hooks/useFeedReleaseData";
import { useComicStarterRelease } from "./hooks/useComicStarterRelease";

interface FeedFullscreenPreviewProps {
  // Caller passes whichever identifier they have. Compact-card flows pass a
  // slug (we resolve to UUID via useComicStarterRelease); the feature
  // comic_release card already has the UUID and passes that directly.
  releaseUuid?: string | null;
  slug?: string | null;
  // Reading direction lookup. Feature cards know it from the feed payload;
  // compact-card flows derive it from the resolved comic record.
  readingDirection?: string;
  // Slot index to mount the inner viewer at — 0 = cover, 1+ = page index.
  // The active feature card passes its current activePage so the fullscreen
  // resumes where the user was already reading.
  initialSlotIndex?: number;
  onClose: () => void;
}

/**
 * Viewport-filling preview of a comic release. Hosts FeedPagesViewer with
 * the surrounding feed chrome stripped — the user reads the comic in a
 * focused mode and dismisses with pinch-in, an aggressive vertical swipe,
 * ESC, or the close button (all wired by FeedFullscreenChrome).
 *
 * Horizontal swipes inside the fullscreen dispatch the regular
 * `feed-page-nav` event so both the fullscreen viewer AND the underlying
 * active feed-card viewer advance — they stay in sync, so closing the
 * fullscreen leaves the underlying card on the same page.
 */
export function FeedFullscreenPreview({
  releaseUuid,
  slug,
  readingDirection,
  initialSlotIndex,
  onClose,
}: FeedFullscreenPreviewProps) {
  const slugResolution = useComicStarterRelease(slug ?? null);
  const resolvedUuid = releaseUuid ?? slugResolution.releaseUuid;
  const resolvedReadingDirection =
    readingDirection ?? slugResolution.comic?.reading_direction ?? "l";

  const { release, pages, isLoading, isPaywalled } = useFeedReleaseData(
    resolvedUuid ?? "",
    !!resolvedUuid,
  );

  const onHorizontalSwipe = useCallback((deltaX: number) => {
    window.dispatchEvent(
      new CustomEvent("feed-page-nav", {
        detail: deltaX > 0 ? "right" : "left",
      }),
    );
  }, []);

  return (
    <FeedFullscreenChrome
      onClose={onClose}
      onHorizontalSwipe={onHorizontalSwipe}
    >
      <Box flex="1" position="relative" overflow="hidden">
        {!resolvedUuid && slugResolution.isLoading ? (
          <Center w="100%" h="100%">
            <Spinner color="orange.400" size="xl" />
          </Center>
        ) : !resolvedUuid ? (
          <Center w="100%" h="100%">
            <Text color="white" opacity={0.7}>
              Couldn&rsquo;t open this comic. Try again.
            </Text>
          </Center>
        ) : (
          <>
            <FeedPagesViewer
              releaseUuid={resolvedUuid}
              pages={pages}
              isPaywalled={isPaywalled}
              isLoading={isLoading}
              isInteractive
              readingDirection={resolvedReadingDirection}
              hasFeedPrev={false}
              hasFeedNext={false}
              release={release}
              initialSlotIndex={initialSlotIndex}
            />
            {/* Comic title + creator + chapter strip pinned to the bottom.
                Card is pointerEvents:none so the gradient doesn't eat
                swipe-down dismisses on the lower half of the screen. */}
            {release?.comic ? (
              <FeedComicInfoCard
                comic={release.comic}
                release={release}
              />
            ) : null}
          </>
        )}
      </Box>
    </FeedFullscreenChrome>
  );
}
